import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web'

/**
 * ADR-0024 D10 - telemetria de navegador correlacionada ao backend.
 *
 * A correlacao usa o header `X-Request-ID`: o `CorrelationIdMiddleware` da API
 * da precedencia ao header enviado pelo cliente, e esse valor E o `trace_id`
 * OTEL canonico (ADR-0023 D9). Um erro de tela chega, em um clique, ate a
 * requisicao, o span de banco e o evento de auditoria correspondentes.
 *
 * GUARDA D10.1 (LGPD, deriva do ADR-0014): o Application Insights JS coleta
 * automaticamente a URL da pagina. Rotas do frontend NUNCA carregam CPF, e-mail
 * ou nome - apenas identificador opaco. Adicionalmente, o initializer abaixo
 * remove query string antes do envio, como segunda camada de defesa.
 *
 * GUARDA D10.3 (ADR-0021): a connection string contem
 * `InstrumentationKey=<UUID canonico>` e NUNCA e versionada - chega por
 * variavel de ambiente do build.
 */

let client: ApplicationInsights | null = null

/** Remove query string e fragmento de qualquer URL enviada na telemetria. */
function stripUrlSensitiveParts(rawUrl: string): string {
  const queryIndex = rawUrl.search(/[?#]/)
  return queryIndex === -1 ? rawUrl : rawUrl.slice(0, queryIndex)
}

function sanitizeTelemetry(item: ITelemetryItem): boolean {
  const baseData = item.baseData
  if (baseData) {
    if (typeof baseData.uri === 'string') {
      baseData.uri = stripUrlSensitiveParts(baseData.uri)
    }
    if (typeof baseData.refUri === 'string') {
      baseData.refUri = stripUrlSensitiveParts(baseData.refUri)
    }
    if (typeof baseData.target === 'string') {
      baseData.target = stripUrlSensitiveParts(baseData.target)
    }
  }
  if (item.ext?.trace?.name && typeof item.ext.trace.name === 'string') {
    item.ext.trace.name = stripUrlSensitiveParts(item.ext.trace.name)
  }
  return true
}

/**
 * Inicializa a telemetria. Silenciosamente inerte se a connection string nao
 * estiver configurada - o que e o caso em desenvolvimento e em teste.
 */
export function initTelemetry(): ApplicationInsights | null {
  if (client) return client

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING
  if (!connectionString) return null

  client = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      disableCookiesUsage: true,
      // s53 A16: true acrescenta traceparent/Request-Id/Request-Context nas chamadas a API
      // (outra origem) e o CORS da API recusa o preflight. A correlacao ja vem do X-Request-ID.
      enableCorsCorrelation: false,
      autoTrackPageVisitTime: true,
    },
  })
  client.loadAppInsights()
  client.addTelemetryInitializer(sanitizeTelemetry)
  client.trackPageView()
  return client
}

export function isTelemetryEnabled(): boolean {
  return client !== null
}

export function trackError(error: Error, requestId?: string): void {
  client?.trackException({
    exception: error,
    properties: requestId ? { requestId } : undefined,
  })
}

/** Exportado apenas para teste (ADR-0024 D13). */
export const __testables = { stripUrlSensitiveParts, sanitizeTelemetry }
